import { NavLink } from 'react-router-dom';

import type { PumsemSearchHit } from '../model/pumsem.type';
import { PumsemChangeBadge } from './PumsemChangeBadge';

interface PumsemResultListProps {
  hits: PumsemSearchHit[];
  /** 결과 카드가 물고 갈 검색 조건. 상세에서 뒤로 오면 목록이 그대로 있어야 한다. */
  buildHref: (division: string, code: string) => string;
  /** 직종 배지 클릭 → 역검색. */
  onResourceClick: (name: string) => void;
  /** 지금 켜져 있는 역검색 직종. 그 배지를 결과 안에서도 강조한다. */
  activeResource: string | null;
  /** 카드를 눌러 상세로 갈 때. 모바일 드로어를 닫는 데 쓴다. */
  onNavigate?: () => void;
}

const ACTIVE = 'border-l-brand-500 bg-brand-50 dark:bg-brand-500/10';
const IDLE =
  'border-l-transparent hover:bg-neutral-50 dark:hover:bg-white/[0.04]';

/** 검색 결과 목록. 표 본문은 안 싣는다 — 카드 30장이 표 30벌을 끌면 목록이 상세보다 무겁다. */
export function PumsemResultList({
  hits,
  buildHref,
  onResourceClick,
  activeResource,
  onNavigate,
}: PumsemResultListProps) {
  return (
    <ul>
      {hits.map((hit) => (
        <li
          key={`${hit.division.slug}/${hit.code}`}
          className="border-b border-neutral-100 dark:border-white/8"
        >
          {/*
            직종 배지는 링크 **밖**에 둔다. 예전엔 `<NavLink>` 안에 `<button>` 이
            들어 있었다 — `<a>` 는 인터랙티브 요소를 담을 수 없고, 키보드로는
            카드 하나가 링크 1 + 버튼 N 개의 탭 정지점이 되며, 스크린리더가
            링크 이름에 직종명까지 다 읽는다.
          */}
          <NavLink
            to={buildHref(hit.division.slug, hit.code)}
            onClick={onNavigate}
            className={({ isActive }) =>
              `block border-l-[3px] px-4 pt-3 pb-1.5 transition-colors ${isActive ? ACTIVE : IDLE}`
            }
          >
            <div className="mb-1.5 flex items-center gap-1.5">
              <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[12.5px] font-bold text-neutral-600 dark:bg-white/8 dark:text-neutral-300">
                {hit.division.shortTitle}
              </span>
              <span className="text-[13px] tabular-nums text-neutral-400">
                {hit.code}
              </span>
              <PumsemChangeBadge change={hit.changeKind} />
            </div>

            <p className="text-[15.5px] leading-relaxed font-bold text-neutral-900 dark:text-neutral-100">
              {hit.title}
            </p>
          </NavLink>

          <div className="flex flex-wrap items-center gap-1.5 border-l-[3px] border-l-transparent px-4 pb-3">
            {hit.resources.map((resource) => {
              const on = resource.name === activeResource;
              return (
                <button
                  key={`${resource.kind}:${resource.name}`}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onResourceClick(resource.name)}
                  className={`rounded-full border px-2 py-0.5 text-[12.5px] transition-colors ${
                    on
                      ? 'border-brand-400 bg-brand-100 font-bold text-brand-900 dark:border-brand-500/40 dark:bg-brand-500/20 dark:text-brand-100'
                      : 'border-neutral-300 text-neutral-600 hover:border-neutral-400 dark:border-white/15 dark:text-neutral-300'
                  }`}
                >
                  {resource.name}
                </button>
              );
            })}
            {/* 서술 항목(적용기준 계열)은 단위가 없다. 그대로 그리면 `당` 한
                글자만 남는다 — 상세 화면은 이미 막아 뒀는데 목록만 빠져 있었다. */}
            {hit.unit && (
              <span className="px-1 text-[12.5px] text-neutral-400">
                {hit.unit}당
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
